import { createContext, useContext, useState, useEffect, useCallback } from "react";

const CreatorsContext = createContext(null);

// Use relative URL for API calls - works on any domain
const getApiUrl = () => {
  return '';
};

export const CreatorsProvider = ({ children }) => {
  const [creators, setCreators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get auth credentials for admin requests
  const getAuthHeaders = () => {
    const auth = localStorage.getItem("jglinks_auth");
    const credsStr = localStorage.getItem("jglinks_creds");
    
    if (auth === "true" && credsStr) {
      try {
        const creds = JSON.parse(credsStr);
        const credentials = btoa(`${creds.username}:${creds.password}`);
        return {
          "Authorization": `Basic ${credentials}`,
          "Content-Type": "application/json"
        };
      } catch (e) {
        console.error("Error parsing credentials:", e);
      }
    }
    return { "Content-Type": "application/json" };
  };

  // Fetch all creators (public)
  const fetchCreators = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${getApiUrl()}/api/creators`);
      if (!response.ok) throw new Error("Failed to fetch creators");
      const data = await response.json();
      setCreators(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching creators:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch all creators including inactive (admin)
  const fetchAllCreators = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${getApiUrl()}/api/admin/creators`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized");
        }
        throw new Error("Failed to fetch creators");
      }
      const data = await response.json();
      setCreators(data);
      setError(null);
      return data;
    } catch (err) {
      console.error("Error fetching all creators:", err);
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load creators on mount
  useEffect(() => {
    fetchCreators();
  }, [fetchCreators]);

  // Get all active creators
  const getActiveCreators = () => {
    return creators.filter(c => c.active);
  };

  // Get featured creators for landing page
  const getFeaturedCreators = () => {
    return creators.filter(c => c.active && c.featured);
  };

  // Get creator by username
  const getCreatorByUsername = (username) => {
    return creators.find(c => c.username.toLowerCase() === username.toLowerCase() && c.active);
  };

  // Get creator by ID
  const getCreatorById = (id) => {
    return creators.find(c => c.id === id);
  };

  // Fetch single creator by ID from server (admin)
  const fetchCreatorById = async (id) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/admin/creators/${id}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error("Failed to fetch creator");
      }
      const creator = await response.json();
      // Update local state
      setCreators(prev => {
        const exists = prev.find(c => c.id === id);
        if (exists) {
          return prev.map(c => c.id === id ? creator : c);
        }
        return [...prev, creator];
      });
      return creator;
    } catch (err) {
      console.error("Error fetching creator:", err);
      return null;
    }
  };

  // Add new creator (admin)
  const addCreator = async (creatorData) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/admin/creators`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(creatorData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create creator");
      }
      
      const newCreator = await response.json();
      setCreators(prev => [...prev, newCreator]);
      return newCreator;
    } catch (err) {
      console.error("Error creating creator:", err);
      throw err;
    }
  };

  // Update creator (admin)
  const updateCreator = async (id, creatorData) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/admin/creators/${id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(creatorData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to update creator");
      }
      
      const updatedCreator = await response.json();
      setCreators(prev => prev.map(c => c.id === id ? updatedCreator : c));
      return updatedCreator;
    } catch (err) {
      console.error("Error updating creator:", err);
      throw err;
    }
  };

  // Delete creator (admin)
  const deleteCreator = async (id) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/admin/creators/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error("Failed to delete creator");
      }
      
      setCreators(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error("Error deleting creator:", err);
      throw err;
    }
  };

  // Restore a soft-deleted creator (admin)
  const restoreCreator = async (id) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/admin/creators/${id}/restore`, {
        method: "PATCH",
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error("Failed to restore");
      setCreators(prev => prev.map(c => c.id === id ? { ...c, deleted: false } : c));
    } catch (err) {
      console.error("Error restoring:", err);
      throw err;
    }
  };

  // Toggle creator active status (admin)
  const toggleCreatorStatus = async (id) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/admin/creators/${id}/toggle-status`, {
        method: "PATCH",
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error("Failed to toggle status");
      }
      
      const result = await response.json();
      setCreators(prev => prev.map(c => 
        c.id === id ? { ...c, active: result.active } : c
      ));
    } catch (err) {
      console.error("Error toggling status:", err);
      throw err;
    }
  };

  // Toggle creator featured status (admin)
  const toggleCreatorFeatured = async (id) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/admin/creators/${id}/toggle-featured`, {
        method: "PATCH",
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error("Failed to toggle featured");
      }
      
      const result = await response.json();
      setCreators(prev => prev.map(c => 
        c.id === id ? { ...c, featured: result.featured } : c
      ));
    } catch (err) {
      console.error("Error toggling featured:", err);
      throw err;
    }
  };

  // Toggle creator branding visibility (admin)
  const toggleCreatorBranding = async (id) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/admin/creators/${id}/toggle-branding`, {
        method: "PATCH",
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error("Failed to toggle branding");
      }
      
      const result = await response.json();
      setCreators(prev => prev.map(c => 
        c.id === id ? { ...c, hideBranding: result.hideBranding } : c
      ));
    } catch (err) {
      console.error("Error toggling branding:", err);
      throw err;
    }
  };

  const toggleCreatorShorts = async (id) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/admin/creators/${id}/toggle-shorts`, {
        method: "PATCH",
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error("Failed to toggle shorts");
      const result = await response.json();
      setCreators(prev => prev.map(c =>
        c.id === id ? { ...c, shortsEnabled: result.shortsEnabled } : c
      ));
    } catch (err) {
      console.error("Error toggling shorts:", err);
      throw err;
    }
  };

  // Check if username is available
  const isUsernameAvailable = (username, excludeId = null) => {
    return !creators.some(c => 
      c.username.toLowerCase() === username.toLowerCase() && c.id !== excludeId
    );
  };

  return (
    <CreatorsContext.Provider value={{
      creators,
      isLoading,
      error,
      fetchCreators,
      fetchAllCreators,
      fetchCreatorById,
      getActiveCreators,
      getFeaturedCreators,
      getCreatorByUsername,
      getCreatorById,
      addCreator,
      updateCreator,
      deleteCreator,
      restoreCreator,
      toggleCreatorStatus,
      toggleCreatorFeatured,
      toggleCreatorBranding,
      toggleCreatorShorts,
      isUsernameAvailable
    }}>
      {children}
    </CreatorsContext.Provider>
  );
};

export const useCreators = () => {
  const context = useContext(CreatorsContext);
  if (!context) {
    throw new Error("useCreators must be used within a CreatorsProvider");
  }
  return context;
};
